import React, { useEffect, useMemo, useRef, useState } from "react";

// DAY shift
const DAY_START_MIN = 9 * 60;
const DAY_END_MIN = 16 * 60 + 30;
const DAY_AMBER_MIN = 16 * 60 + 45;

// NIGHT shift
const NIGHT_START_MIN = 15 * 60 + 30;
const NIGHT_END_MIN = 30;
const NIGHT_AMBER_MIN = 2 * 60 + 45;

const ROLLOVER_MIN = 3 * 60;

const DAY_TONE = {
  fill: "var(--ct-ok-bg)",
  edge: "var(--ct-ok)",
  text: "var(--ct-ok)",
};
const NIGHT_TONE = {
  fill: "#dbeafe",
  edge: "#1d4ed8",
  text: "#1d4ed8",
};

type Tone = typeof DAY_TONE;

type PresentPerson = {
  staffId: string;
  name: string;
  surname: string;
  shift: string;
  location: string;
  arrivalMs: number;
  arrivalTime: string;
};

type ExpectedPerson = {
  staffId: string;
  name: string;
  shift: string;
  location: string;
};

type AttendancePayload = {
  date: string;
  weekday: string;
  hasRosterForDate: boolean;
  locations: string[];
  present: PresentPerson[];
  expected: ExpectedPerson[];
  counts: { present: number; expected: number };
};

type AttScoreBranch = {
  location: string;
  expected: number;
  onTime: number;
  late: number;
  noShow: number;
  pct: number;
  hasData: boolean;
  prevExpected: number;
  prevPct: number;
  prevHasData: boolean;
  deltaPp: number;
  trend: "up" | "down" | "flat";
};
type AttScore = { month: string; branches: AttScoreBranch[] };

function sastNowMinutes(): number {
  const nowSast = new Date(
    new Date().toLocaleString("en-US", { timeZone: "Africa/Johannesburg" })
  );
  return nowSast.getHours() * 60 + nowSast.getMinutes();
}

function hhmm(t: string): string {
  const parts = String(t || "").split(":");
  if (parts.length < 2) return t || "";
  return parts[0].padStart(2, "0") + ":" + parts[1].padStart(2, "0");
}

function displayName(name: string, surname?: string): string {
  const n = (name || "").trim();
  const s = (surname || "").trim();
  if (!s) return n;
  return `${n} ${s.charAt(0)}.`;
}

function initialsOf(name: string, surname?: string): string {
  const n = (name || "").trim();
  const s = (surname || "").trim();
  const first = n.charAt(0) || "?";
  const second = s.charAt(0) || n.charAt(1) || "";
  return (first + second).toUpperCase();
}

// -----------------------------------------------------------------------
// Read the logged-in person's branch from the JWT. Returns the branch
// name as the attendance feed uses it ("Gardens", "Plumstead", "Parklands",
// "Sea Point") or null for unrestricted users (the operator).
// -----------------------------------------------------------------------
const BRANCH_ID_TO_NAME: Record<string, string> = {
  GRD: "Gardens",
  PLM: "Plumstead",
  PRK: "Parklands",
  SEA: "Sea Point",
};

function getUserBranchName(): string | null {
  try {
    const raw = localStorage.getItem("gotrue.user");
    if (!raw) return null;
    const user = JSON.parse(raw);
    const token: string | undefined = user?.token?.access_token;
    if (!token) return null;
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    const roles: unknown[] = payload?.app_metadata?.roles ?? [];
    if (!Array.isArray(roles)) return null;
    for (const r of roles) {
      const m = String(r).match(/^branch([A-Za-z]{3})$/);
      if (m) return BRANCH_ID_TO_NAME[m[1].toUpperCase()] || null;
    }
    return null;
  } catch {
    return null;
  }
}

function Avatar({
  label,
  present,
}: {
  label: string;
  present: boolean;
}) {
  return (
    <span
      style={{
        width: 34,
        height: 34,
        borderRadius: "50%",
        flexShrink: 0,
        display: "grid",
        placeItems: "center",
        fontSize: 12.5,
        fontWeight: 650,
        letterSpacing: "0.02em",
        background: present ? "rgba(255,255,255,0.7)" : "transparent",
        border: present ? "none" : "1px dashed var(--ct-line)",
        color: present ? "var(--ct-ink-2)" : "var(--ct-ink-3)",
      }}
    >
      {label}
    </span>
  );
}

// Month-to-date attendance discipline per branch. Higher is better, so
// up = green (improving), down = red (slipping) — opposite of late orders.
function MonthlyAttendanceStrip({
  data,
  lockedBranch,
}: {
  data: AttScore | null;
  lockedBranch: string | null;
}) {
  if (!data || !Array.isArray(data.branches) || data.branches.length === 0) return null;
  let list = data.branches;
  if (lockedBranch) list = list.filter((b) => b.location === lockedBranch);
  if (list.length === 0) return null;

  const card: React.CSSProperties = {
    flex: "1 1 190px",
    minWidth: 190,
    borderRadius: 10,
    border: "1px solid var(--ct-line)",
    background: "var(--ct-surface)",
    padding: "14px 16px",
  };

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ct-ink-2)", marginBottom: 10 }}>
        Attendance discipline — month to date{" "}
        <span style={{ color: "var(--ct-ink-3)", fontWeight: 400 }}>· {data.month}</span>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        {list.map((b) => {
          if (!b.hasData) {
            return (
              <div key={b.location} style={card}>
                <div style={{ fontSize: 12.5, fontWeight: 500, color: "var(--ct-ink-2)", marginBottom: 8 }}>
                  {b.location}
                </div>
                <div className="ct-num" style={{ fontSize: 26, fontWeight: 650, color: "var(--ct-ink-3)", lineHeight: 1.1 }}>
                  —
                </div>
                <div style={{ fontSize: 12, color: "var(--ct-ink-3)", marginTop: 6 }}>awaiting clock data</div>
              </div>
            );
          }

          const good = b.trend === "up";
          const bad = b.trend === "down";
          const arrow = good ? "▲" : bad ? "▼" : "—";
          const noBaseline = !b.prevHasData;
          const trendColor = noBaseline
            ? "var(--ct-ink-3)"
            : good
            ? "var(--ct-ok)"
            : bad
            ? "var(--ct-crit)"
            : "var(--ct-ink-3)";

          return (
            <div key={b.location} style={card}>
              <div style={{ fontSize: 12.5, fontWeight: 500, color: "var(--ct-ink-2)", marginBottom: 8 }}>
                {b.location}
              </div>
              <div
                className="ct-num"
                style={{ fontSize: 26, fontWeight: 650, letterSpacing: "-0.02em", lineHeight: 1.1, color: "var(--ct-ink)" }}
              >
                {b.pct}%
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: trendColor, marginTop: 6, whiteSpace: "nowrap" }}>
                {noBaseline ? (
                  <span style={{ fontWeight: 400 }}>no last-month data</span>
                ) : (
                  <>
                    {arrow} {Math.abs(b.deltaPp)} pp{" "}
                    <span style={{ color: "var(--ct-ink-3)", fontWeight: 400 }}>vs last month</span>
                  </>
                )}
              </div>
              <div style={{ fontSize: 11.5, color: "var(--ct-ink-3)", marginTop: 4 }}>
                {b.onTime} on time · {b.late} late · {b.noShow} no-show
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const attendanceCache: {
  [dateKey: string]: {
    data: AttendancePayload | null;
    seen: Set<string>;
  };
} = {};
let lastChosenLocation = "";

export function AttendanceBoard({ date }: { date?: string }) {
  const dateKey = date || "today";
  const cached = attendanceCache[dateKey];

  const [data, setData] = useState<AttendancePayload | null>(
    cached ? cached.data : null
  );
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState<string>("");

  // If this person has a branch tag, lock them to that branch.
  const lockedBranch = useMemo(() => getUserBranchName(), []);

  const [location, setLocation] = useState<string>(
    lockedBranch || lastChosenLocation
  );

  const [, setTick] = useState(0);

  const [scoreData, setScoreData] = useState<AttScore | null>(null);
  useEffect(() => {
    let alive = true;
    fetch("/.netlify/functions/get-attendance-score")
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (alive && j) setScoreData(j as AttScore); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // ── CSV export (operators only) ────────────────────────────────────
  const nowMonthSAST = () => {
    const n = new Date(new Date().toLocaleString("en-US", { timeZone: "Africa/Johannesburg" }));
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`;
  };
  const [exportMonth, setExportMonth] = useState<string>(nowMonthSAST());
  const [exporting, setExporting] = useState(false);
  const exportClockins = async () => {
    setExporting(true);
    try {
      const res = await fetch(`/.netlify/functions/export-attendance?month=${encodeURIComponent(exportMonth)}`);
      if (!res.ok) throw new Error(await res.text());
      const text = await res.text();
      const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `clock-ins-${exportMonth}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      console.error("EXPORT_CLOCKINS:", e);
    } finally {
      setExporting(false);
    }
  };

  const seenRef = useRef<Set<string>>(cached ? cached.seen : new Set());

  useEffect(() => {
    let alive = true;
    if (!attendanceCache[dateKey]) {
      seenRef.current = new Set();
      setLoading(true);
    }
    setError("");

    const load = async () => {
      try {
        const url =
          "/.netlify/functions/get-attendance?date=" +
          encodeURIComponent(date || "");
        const res = await fetch(url);
        if (!res.ok) throw new Error(await res.text());
        const json = (await res.json()) as AttendancePayload;
        if (!alive) return;
        setData(json);
        setError("");
        attendanceCache[dateKey] = {
          data: json,
          seen: seenRef.current,
        };
      } catch (e: any) {
        if (!alive) return;
        setError("reconnecting");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    const iv = setInterval(load, 30000);
    return () => {
      alive = false;
      clearInterval(iv);
    };
  }, [date]);

  useEffect(() => {
    const iv = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(iv);
  }, []);

  const branches = useMemo(() => {
    if (!data) return [];
    const all = Array.isArray(data.locations) && data.locations.length > 0
      ? data.locations
      : [];
    // If locked to a branch, only show that one.
    if (lockedBranch) return all.filter((b) => b === lockedBranch);
    return all;
  }, [data, lockedBranch]);

  useEffect(() => {
    if (branches.length === 0) return;
    if (!location || !branches.includes(location)) {
      setLocation(branches[0]);
      lastChosenLocation = branches[0];
    }
  }, [branches]);

  const chooseLocation = (loc: string) => {
    setLocation(loc);
    lastChosenLocation = loc;
  };

  const nowMin = sastNowMinutes();

  const missedCheckout = (p: PresentPerson): boolean => {
    if (p.shift === "Day-Shift") {
      return nowMin >= DAY_AMBER_MIN && nowMin < ROLLOVER_MIN;
    }
    return nowMin >= NIGHT_AMBER_MIN && nowMin < ROLLOVER_MIN;
  };

  const dayStatus = (): "UPCOMING" | "ACTIVE" | "ENDED" => {
    if (nowMin < DAY_START_MIN) return "UPCOMING";
    if (nowMin < DAY_END_MIN) return "ACTIVE";
    return "ENDED";
  };
  const nightStatus = (): "UPCOMING" | "ACTIVE" | "ENDED" => {
    if (nowMin >= NIGHT_START_MIN) return "ACTIVE";
    if (nowMin < NIGHT_END_MIN) return "ACTIVE";
    if (nowMin < ROLLOVER_MIN) return "ENDED";
    return "UPCOMING";
  };

  const countsByBranch = useMemo(() => {
    const out: Record<string, number> = {};
    if (!data) return out;
    for (const p of data.present) {
      out[p.location] = (out[p.location] || 0) + 1;
    }
    return out;
  }, [data]);

  const benches = useMemo(() => {
    const empty = {
      day: { present: [] as PresentPerson[], noShow: [] as ExpectedPerson[] },
      night: { present: [] as PresentPerson[], noShow: [] as ExpectedPerson[] },
      onFloor: 0,
    };
    if (!data || !location) return empty;

    const here = data.present.filter((p) => p.location === location);
    const wanted = data.expected.filter((e) => e.location === location);

    const dayPresent = here
      .filter((p) => p.shift === "Day-Shift")
      .sort((a, b) => a.arrivalMs - b.arrivalMs);
    const nightPresent = here
      .filter((p) => p.shift === "Night-Shift")
      .sort((a, b) => a.arrivalMs - b.arrivalMs);

    const dayIds = new Set(dayPresent.map((p) => p.staffId));
    const nightIds = new Set(nightPresent.map((p) => p.staffId));

    const dayNoShow = wanted.filter(
      (e) => e.shift === "Day-Shift" && !dayIds.has(e.staffId)
    );
    const nightNoShow = wanted.filter(
      (e) => e.shift === "Night-Shift" && !nightIds.has(e.staffId)
    );

    return {
      day: { present: dayPresent, noShow: dayNoShow },
      night: { present: nightPresent, noShow: nightNoShow },
      onFloor: here.length,
    };
  }, [data, location, nowMin]);

  useEffect(() => {
    if (!data) return;
    const ids = data.present.map((p) => p.staffId + "||" + p.location);
    const t = setTimeout(() => {
      ids.forEach((id) => seenRef.current.add(id));
    }, 1200);
    return () => clearTimeout(t);
  }, [data]);

  return (
    <div>
      <style>{`
        @keyframes ab-pop {
          0%   { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes ab-spin { to { transform: rotate(360deg); } }
        .ab-spinner {
          width: 30px; height: 30px; border-radius: 50%;
          border: 2px solid var(--ct-line);
          border-top-color: var(--ct-accent);
          animation: ab-spin .8s linear infinite;
          margin: 0 auto;
        }
        .ab-select {
          border: 1px solid var(--ct-line);
          background: var(--ct-surface);
          color: var(--ct-ink);
          border-radius: 8px;
          padding: 8px 12px;
          font-family: inherit;
          font-size: 13.5px;
          font-weight: 500;
          cursor: pointer;
          min-width: 220px;
          outline: none;
        }
        .ab-select:focus {
          border-color: var(--ct-accent);
          box-shadow: 0 0 0 3px var(--ct-accent-bg);
        }
      `}</style>

      {/* header row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          flexWrap: "wrap",
          marginBottom: 20,
        }}
      >
        {/* Show the dropdown only if the user can pick between branches.
            Managers locked to one branch see a label instead. */}
        {branches.length > 1 && (
          <select
            className="ab-select"
            value={location}
            onChange={(e) => chooseLocation(e.target.value)}
          >
            {branches.map((b) => (
              <option key={b} value={b}>
                {b} — {countsByBranch[b] || 0} on the floor
              </option>
            ))}
          </select>
        )}

        {branches.length === 1 && (
          <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ct-ink)" }}>
            {branches[0]} — {countsByBranch[branches[0]] || 0} on the floor
          </div>
        )}

        <div style={{ fontSize: 13, color: "var(--ct-ink-3)" }}>
          {data && !lockedBranch
            ? `${data.counts.present} on the floor across all branches`
            : ""}
        </div>

        {/* Clock-in CSV export — operators only */}
        {!lockedBranch && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
            <input
              type="month"
              value={exportMonth}
              onChange={(e) => setExportMonth(e.target.value)}
              style={{
                border: "1px solid var(--ct-line)",
                background: "var(--ct-surface)",
                color: "var(--ct-ink)",
                borderRadius: 8,
                padding: "7px 10px",
                fontFamily: "inherit",
                fontSize: 13,
              }}
            />
            <button
              onClick={exportClockins}
              disabled={exporting}
              style={{
                border: "1px solid var(--ct-line)",
                background: "var(--ct-surface)",
                color: "var(--ct-ink)",
                borderRadius: 8,
                padding: "7px 13px",
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: 600,
                cursor: exporting ? "default" : "pointer",
              }}
            >
              {exporting ? "Exporting…" : "Export clock-ins (CSV)"}
            </button>
          </div>
        )}
      </div>

      <MonthlyAttendanceStrip data={scoreData} lockedBranch={lockedBranch} />

      {error === "reconnecting" && data && (
        <div
          style={{
            border: "1px solid var(--ct-line)",
            background: "var(--ct-warn-bg)",
            borderRadius: 10,
            padding: "10px 14px",
            color: "var(--ct-warn)",
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          Reconnecting to the attendance feed — showing the last known lineup.
        </div>
      )}

      {error === "reconnecting" && !data && (
        <div
          style={{
            border: "1px solid var(--ct-line)",
            background: "var(--ct-crit-bg)",
            borderRadius: 10,
            padding: "14px 16px",
            color: "var(--ct-crit)",
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          Couldn't reach the attendance feed. Retrying…
        </div>
      )}

      {!data && !error && (
        <div style={{ padding: "48px 0", textAlign: "center" }}>
          <div className="ab-spinner" />
          <div
            style={{
              color: "var(--ct-ink-3)",
              fontSize: 13.5,
              marginTop: 14,
            }}
          >
            Loading…
          </div>
        </div>
      )}

      {data && location && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 16,
          }}
        >
          <Bench
            title="Day shift"
            tone={DAY_TONE}
            status={dayStatus()}
            present={benches.day.present}
            noShow={benches.day.noShow}
            seen={seenRef.current}
            missedCheckout={missedCheckout}
          />
          <Bench
            title="Night shift"
            tone={NIGHT_TONE}
            status={nightStatus()}
            present={benches.night.present}
            noShow={benches.night.noShow}
            seen={seenRef.current}
            missedCheckout={missedCheckout}
          />
        </div>
      )}

      {data && branches.length === 0 && (
        <div
          style={{
            color: "var(--ct-ink-3)",
            padding: "48px 0",
            textAlign: "center",
            fontSize: 13.5,
          }}
        >
          Nobody on the floor at any branch yet.
        </div>
      )}

      {data && !data.hasRosterForDate && (
        <div
          style={{
            marginTop: 18,
            color: "var(--ct-ink-3)",
            fontSize: 12.5,
          }}
        >
          No roster loaded for this week, so no-show cards can't be shown for{" "}
          {data.date}. Present staff are unaffected.
        </div>
      )}
    </div>
  );
}

function Bench({
  title,
  tone,
  status,
  present,
  noShow,
  seen,
  missedCheckout,
}: {
  title: string;
  tone: Tone;
  status: "UPCOMING" | "ACTIVE" | "ENDED";
  present: PresentPerson[];
  noShow: ExpectedPerson[];
  seen: Set<string>;
  missedCheckout: (p: PresentPerson) => boolean;
}) {
  const active = status === "ACTIVE";

  const badge = active
    ? { bg: tone.fill, fg: tone.text, label: "Active" }
    : status === "UPCOMING"
      ? { bg: "var(--ct-surface-3)", fg: "var(--ct-ink-3)", label: "Upcoming" }
      : { bg: "var(--ct-surface-3)", fg: "var(--ct-ink-2)", label: "Ended" };

  return (
    <div
      style={{
        border: "1px solid var(--ct-line)",
        borderRadius: 10,
        background: active ? "var(--ct-surface)" : "var(--ct-surface-2)",
        overflow: "hidden",
        transition: "background .3s ease",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          padding: "13px 16px",
          borderBottom: "1px solid var(--ct-line)",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: "-0.01em",
              color: active ? "var(--ct-ink)" : "var(--ct-ink-2)",
            }}
          >
            {title}
          </span>
          <span
            style={{
              fontSize: 11.5,
              fontWeight: 600,
              padding: "3px 9px",
              borderRadius: 20,
              background: badge.bg,
              color: badge.fg,
            }}
          >
            {badge.label}
          </span>
        </span>
        <span style={{ color: "var(--ct-ink-3)", fontSize: 12.5 }}>
          {present.length} in · {noShow.length} out
        </span>
      </div>

      <div style={{ padding: 12 }}>
        {present.length === 0 && noShow.length === 0 && (
          <div
            style={{
              color: "var(--ct-ink-3)",
              fontSize: 13.5,
              padding: "24px 4px",
              textAlign: "center",
            }}
          >
            Nobody on this bench yet.
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {present.map((p, i) => {
            const alreadySeen = seen.has(p.staffId + "||" + p.location);
            const overdue = missedCheckout(p);
            const lit = active && !overdue;

            return (
              <div
                key={p.staffId + p.shift + p.location}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 12px",
                  borderRadius: 9,
                  border: lit
                    ? `1px solid ${tone.edge}`
                    : "1px solid var(--ct-line)",
                  background: lit ? tone.fill : "var(--ct-surface)",
                  animation: alreadySeen
                    ? "none"
                    : `ab-pop .32s ease ${i * 0.05}s both`,
                  transition: "background .3s ease, border-color .3s ease",
                }}
              >
                <Avatar label={initialsOf(p.name, p.surname)} present />

                <span style={{ minWidth: 0, flex: 1 }}>
                  <span
                    style={{
                      display: "block",
                      color: "var(--ct-ink)",
                      fontSize: 14,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {displayName(p.name, p.surname)}
                  </span>
                  <span
                    className="ct-num"
                    style={{
                      display: "block",
                      color: lit ? "var(--ct-ink-2)" : "var(--ct-ink-3)",
                      fontSize: 12.5,
                      marginTop: 1,
                    }}
                  >
                    In at {hhmm(p.arrivalTime)}
                  </span>
                </span>

                {overdue ? (
                  <span
                    style={{
                      color: "var(--ct-ink-3)",
                      fontSize: 11.5,
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                    }}
                  >
                    No check-out
                  </span>
                ) : (
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: lit ? tone.edge : "var(--ct-ink-3)",
                      flexShrink: 0,
                    }}
                  />
                )}
              </div>
            );
          })}

          {noShow.map((e) => (
            <div
              key={"ns-" + e.staffId + e.shift + e.location}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 12px",
                borderRadius: 9,
                border: "1px dashed var(--ct-line)",
                background: "var(--ct-surface-3)",
              }}
            >
              <Avatar label={initialsOf(e.name)} present={false} />

              <span style={{ minWidth: 0, flex: 1 }}>
                <span
                  style={{
                    display: "block",
                    color: "var(--ct-ink-2)",
                    fontSize: 14,
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {e.name}
                </span>
                <span
                  style={{
                    display: "block",
                    color: "var(--ct-ink-3)",
                    fontSize: 12.5,
                    marginTop: 1,
                  }}
                >
                  Not clocked in
                </span>
              </span>

              <span
                style={{
                  color: "var(--ct-ink-3)",
                  fontSize: 11.5,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {status === "UPCOMING" ? "Upcoming" : "No-show"}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
